// React
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

// MUI
import {
  ApartmentRounded,
  ArrowUpwardRounded,
  AutoAwesome,
  BusinessCenterOutlined,
  GroupsOutlined,
  NotificationsNoneRounded,
  StopRounded,
  TaskAltRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import AiMessage from "./AiMessage";
import {
  MAX_MESSAGE_CHARS,
  resetAiConversation,
  retryAiMessage,
  sendAiMessage,
  stopAiMessage,
  useAiStore,
} from "./aiStore";

// After this long, the "thinking" line says that it takes longer than usual
const SLOW_AFTER_MS = 15000;
const MAX_TEXTAREA_HEIGHT = { panel: 160, page: 240 };

// Panel: three suggestions for the page the user is on
const CLIENT_SUGGESTIONS = ["summary", "overdue", "notes"];
const GENERAL_SUGGESTIONS = ["today", "standby", "notifications"];
// Page: groups of suggestions (aiAssistant:page.groups.<group>.items.<key>)
const PAGE_GROUPS = [
  { key: "work", icon: TaskAltRounded, items: ["today", "overdue", "week"] },
  { key: "clients", icon: ApartmentRounded, items: ["standby", "byStatus", "renewals"] },
  { key: "notifications", icon: NotificationsNoneRounded, items: ["unread", "digest"] },
  { key: "team", icon: GroupsOutlined, items: ["byTeam", "teamClients"] },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const chipClass =
  "rounded-full border border-gray-200 bg-white px-3 py-1.5 text-left text-[13px] text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-primary";

function Thinking({ since }) {
  const { t } = useTranslation("aiAssistant");
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    const left = SLOW_AFTER_MS - (Date.now() - (since || Date.now()));
    if (left <= 0) {
      setSlow(true);
      return undefined;
    }
    const timer = setTimeout(() => setSlow(true), left);
    return () => clearTimeout(timer);
  }, [since]);

  return (
    <div className="flex items-center gap-2 text-[13px] text-gray-500" role="status">
      <span className="flex items-center gap-1" aria-hidden="true">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="h-1.5 w-1.5 rounded-full bg-gray-400 motion-safe:animate-pulse"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>
      <span>{slow ? t("thinking.slow") : t("thinking.label")}</span>
    </div>
  );
}

function PanelEmpty({ isClientPage, onSend }) {
  const { t } = useTranslation("aiAssistant");
  const keys = isClientPage ? CLIENT_SUGGESTIONS : GENERAL_SUGGESTIONS;
  const group = isClientPage ? "client" : "general";
  return (
    <div className="flex min-h-full flex-col justify-end gap-4">
      <div>
        <span className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-soft text-primary">
          <AutoAwesome sx={{ fontSize: 18 }} aria-hidden="true" />
        </span>
        <p className="text-[15px] font-semibold text-gray-900">{t("empty.title")}</p>
        <p className="mt-1 text-[13px] text-gray-500">{t("empty.subtitle")}</p>
      </div>
      <div>
        <p className="mb-2 text-[12px] font-medium tracking-wide text-gray-400 uppercase">
          {t("suggestions.title")}
        </p>
        <div className="flex flex-wrap gap-2">
          {keys.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onSend(t(`suggestions.${group}.${key}.prompt`))}
              className={chipClass}
            >
              {t(`suggestions.${group}.${key}.label`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SuggestionGroup({ title, icon: Icon, prompts, onSend }) {
  return (
    <div className="surface-card p-4">
      <p className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-gray-900">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary-soft text-primary-strong">
          <Icon sx={{ fontSize: 16 }} aria-hidden="true" />
        </span>
        {title}
      </p>
      <ul className="space-y-1">
        {prompts.map(({ key, label, prompt }) => (
          <li key={key}>
            <button
              type="button"
              onClick={() => onSend(prompt)}
              className="w-full rounded-lg px-2 py-1.5 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-primary"
            >
              {label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PageEmpty({ isClientPage, companyName, onSend }) {
  const { t } = useTranslation("aiAssistant");
  const firstName = useSelector((state) => state.profile?.user?.first_name);
  const groups = PAGE_GROUPS.map(({ key, icon, items }) => ({
    key,
    icon,
    title: t(`page.groups.${key}.title`),
    prompts: items.map((item) => ({
      key: item,
      label: t(`page.groups.${key}.items.${item}.label`),
      prompt: t(`page.groups.${key}.items.${item}.prompt`),
    })),
  }));
  if (isClientPage) {
    groups.unshift({
      key: "client",
      icon: BusinessCenterOutlined,
      title: companyName
        ? t("page.groups.client.title", { name: companyName })
        : t("page.groups.client.titleNoName"),
      prompts: CLIENT_SUGGESTIONS.map((item) => ({
        key: item,
        label: t(`suggestions.client.${item}.label`),
        prompt: t(`suggestions.client.${item}.prompt`),
      })),
    });
  }
  return (
    <div className="flex min-h-full flex-col justify-center py-6">
      <div className="mb-6 text-center sm:mb-8">
        <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary">
          <AutoAwesome sx={{ fontSize: 24 }} aria-hidden="true" />
        </span>
        <h2 className="text-[22px] leading-tight font-semibold tracking-tight text-gray-900 sm:text-[26px]">
          {firstName ? t("page.greeting", { name: firstName }) : t("page.greetingNoName")}
        </h2>
        <p className="mx-auto mt-2 max-w-[520px] text-[14px] text-gray-500">{t("page.subtitle")}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {groups.map((group) => (
          <SuggestionGroup
            key={group.key}
            title={group.title}
            icon={group.icon}
            prompts={group.prompts}
            onSend={onSend}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The conversation with the AI assistant, shared by the panel (AiPanel) and the Chat page:
 * header (render prop), messages with the generative UI blocks, follow-up suggestions, empty
 * state and composer. Renders siblings for a flex column parent.
 * - variant "panel" | "page": compact or full blocks, layout and empty state.
 * - context: sent with every question (path, company, surface).
 * - renderHeader({ onReset, canReset }): optional header above the messages.
 * - composerTop: node above the field (client context line or chip).
 */
export default function AiConversation({
  variant = "panel",
  context,
  onNavigate,
  inputRef,
  renderHeader,
  composerTop,
}) {
  const { t } = useTranslation("aiAssistant");
  const { messages, pending, pendingSince } = useAiStore();
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);
  const hintId = useId();
  const isPage = variant === "page";

  const isClientPage = Boolean(context?.company_id);
  const lastMessage = messages[messages.length - 1];
  const maxHeight = MAX_TEXTAREA_HEIGHT[variant] || MAX_TEXTAREA_HEIGHT.panel;

  // Field height follows the text, up to the max height
  useLayoutEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, maxHeight)}px`;
  }, [draft, inputRef, maxHeight]);

  // Keeps the latest message in view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTo({
      top: list.scrollHeight,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [messages.length, pending]);

  const send = (text) => {
    // A suggestion sent while typing leaves the draft alone
    if (sendAiMessage(text, context) && text === draft) setDraft("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    send(draft);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send(draft);
    }
  };

  const handleStop = () => {
    const text = stopAiMessage();
    if (text && !draft.trim()) setDraft(text);
    inputRef.current?.focus();
  };

  const handleReset = () => {
    resetAiConversation();
    setDraft("");
    inputRef.current?.focus();
  };

  const tooLong = draft.length > MAX_MESSAGE_CHARS;
  const canSend = Boolean(draft.trim()) && !pending && !tooLong;
  const column = isPage ? "mx-auto w-full max-w-[880px]" : "";

  return (
    <>
      {renderHeader?.({ onReset: handleReset, canReset: Boolean(messages.length || pending) })}

      {/* Messages */}
      <div
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-busy={pending}
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${isPage ? "px-1 py-4 sm:px-2" : "px-4 py-4"}`}
      >
        <div className={`${column} ${isPage && !messages.length ? "h-full" : ""}`}>
          {messages.length === 0 && !pending ? (
            isPage ? (
              <PageEmpty
                isClientPage={isClientPage}
                companyName={context?.company_name}
                onSend={send}
              />
            ) : (
              <PanelEmpty isClientPage={isClientPage} onSend={send} />
            )
          ) : (
            <div className={isPage ? "space-y-6" : "space-y-4"}>
              {messages.map((message) => (
                <AiMessage
                  key={message.id}
                  message={message}
                  variant={variant}
                  onNavigate={onNavigate}
                  canRetry={message === lastMessage && !pending}
                  onRetry={() => retryAiMessage(context)}
                  showSuggestions={message === lastMessage && !pending}
                  onSuggest={send}
                />
              ))}
              {pending && <Thinking since={pendingSince} />}
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSubmit}
        className={`shrink-0 ${isPage ? "px-1 pt-2 pb-1 sm:px-2" : "px-3 pt-1 pb-3"}`}
      >
        <div className={column}>
          {composerTop}
          <div
            className={`field flex items-end gap-2 ${isPage ? "rounded-2xl py-2 pr-2 pl-4 shadow-card" : "py-1.5 pr-1.5"}`}
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={t("input.placeholder")}
              aria-label={t("input.placeholder")}
              aria-describedby={hintId}
              aria-invalid={tooLong || undefined}
              className={`flex-1 resize-none border-0 bg-transparent px-0 text-gray-900 outline-none placeholder:text-gray-500 ${isPage ? "max-h-60 min-h-[40px] py-2 text-[15px] leading-6" : "max-h-40 min-h-[32px] py-1 text-[14px] leading-6"}`}
            />
            {pending ? (
              <Tooltip title={t("input.stop")}>
                <button
                  type="button"
                  onClick={handleStop}
                  aria-label={t("input.stop")}
                  className={`inline-flex shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white transition-colors hover:bg-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${isPage ? "h-10 w-10 rounded-xl" : "h-8 w-8"}`}
                >
                  <StopRounded sx={{ fontSize: isPage ? 20 : 18 }} />
                </button>
              </Tooltip>
            ) : (
              <Tooltip title={t("input.send")}>
                <span>
                  <button
                    type="submit"
                    disabled={!canSend}
                    aria-label={t("input.send")}
                    className={`inline-flex shrink-0 items-center justify-center rounded-lg bg-primary text-white transition-colors hover:bg-primary-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:bg-gray-200 disabled:text-gray-400 ${isPage ? "h-10 w-10 rounded-xl" : "h-8 w-8"}`}
                  >
                    <ArrowUpwardRounded sx={{ fontSize: isPage ? 20 : 18 }} />
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
          <p
            id={hintId}
            className={`mt-1.5 px-1 text-[11px] text-gray-400 ${isPage ? "text-center" : ""}`}
          >
            {tooLong ? (
              <span className="text-danger">{t("input.tooLong", { max: MAX_MESSAGE_CHARS })}</span>
            ) : (
              t("disclaimer")
            )}
            <span className="sr-only"> {isPage ? t("input.hintPage") : t("input.hint")}</span>
          </p>
        </div>
      </form>
    </>
  );
}
